/* STATE */
import { push } from 'connected-react-router';
import { combineReducers } from 'redux';

import { call, put, putResolve, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { ClaimStep1StateTypes } from './step1/ducks';
import { ClaimStep2StateTypes } from './step2/ducks';
import { ClaimStep3StateTypes } from './step3/ducks';
import { ClaimStep4StateTypes } from './step4/ducks';
import { ClaimStep5StateTypes } from './step5/ducks';
import { ClaimStep6StateTypes, getClaimStep6DataActions } from './step6/ducks';
import { ClaimStep7StateTypes } from './step7/ducks';
import { environment } from 'environments/environment';
import { Step1ViewVO } from 'generated/bslEbill/claims/claims1/models';
import { Step2ViewVO } from 'generated/bslEbill/claims/claims2/models';

import { Step5ViewVO } from 'generated/bslEbill/claims/claims5/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { BillingManagementClaimStepsRoute } from 'helpers/routes/modules/billingManagementClaimSteps';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';
import { AppState } from 'helpers/store/models/AppState';
import { history } from 'helpers/store/rootReducer';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { ClaimsCancelClaimPayload } from 'models/ClaimsCommonPayloads';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectFinancialDocumentInvoiceNumExt } from 'saga/billing/detail/ducks';
import { selectBillingCaRefNo } from 'saga/billing/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { ClaimActionResponseVO, Step7ViewVO } from 'generated/bslEbill/claims/claims7/models';
import { Step3ViewVO } from 'generated/bslEbill/claims/claims3/models/step3ViewVO';
import { Step4ViewVO } from 'generated/bslEbill/claims/claims4/models/step4ViewVO';
import { ClaimFileResponseVO } from 'generated/bslEbill/claims/claims4/models/claimFileResponseVO';
import { Step6ViewVO } from 'generated/bslEbill/claims/claims6/models';

export interface ChangeStepPayload {
  step: number;
  invoiceNumber?: string | null;
  claimId?: number | null;
  serviceId?: number | null;
  resetWizard?: boolean;
  fromSummary?: boolean;
}

export interface ClaimsState {
  invoiceNumber: string | null;
  invoiceNumberOld: string | null;
  claimId: number | null;
  claimIdOld: number | null;
  serviceId: number | null;
  caRefNo?: string | null;
  currentStep: number | null;
  canContinue: boolean;
  currentData: any;
  loadingStatus: LoadingStatus;
  initialized: boolean;
  fromSummary: boolean;
  cancelClaimModal: boolean;
  changeData: any;
}

/* ACTION TYPES */
export enum ClaimsStateTypes {
  SetInvoiceNumber = '@@Claims/SET_INVOICE_NUMBER',
  SetInvoiceNumberOld = '@@Claims/SET_INVOICE_NUMBER_OLD',
  SetClaimId = '@@Claims/SET_CLAIM_ID',
  SetClaimIdOld = '@@Claims/SET_CLAIM_ID_OLD',
  SetServiceId = '@@Claims/SET_SERVICE_ID',
  SetCaRefNo = '@@Claims/SET_CA_REF_NO',
  SetCurrentStep = '@@Claims/SET_CURRENT_STEP',
  SetCanContinue = '@@Claims/SET_CAN_CONTINUE',
  InitWizard = '@@Claims/INIT_WIZARD',
  CancelClaim = '@@Claims/CANCEL_CLAIM',
  ResetWizard = '@@Claims/RESET_WIZARD',
  SetLoading = '@@Claims/SET_LOADING',
  SetInitialized = '@@Claims/SET_IS_INITIALIZED',
  SetFromSummary = '@@Claims/SET_FROM_SUMMARY',
}

/* ACTIONS */
export const setInvoiceNumberAction = createActionCreator(ClaimsStateTypes.SetInvoiceNumber);
export const setInvoiceNumberOldAction = createActionCreator(ClaimsStateTypes.SetInvoiceNumberOld);
export const setClaimIdAction = createActionCreator(ClaimsStateTypes.SetClaimId);
export const setClaimIdOldAction = createActionCreator(ClaimsStateTypes.SetClaimIdOld);
export const setServiceIdAction = createActionCreator(ClaimsStateTypes.SetServiceId);
export const setCaRefNoAction = createActionCreator(ClaimsStateTypes.SetCaRefNo);
export const setCurrentStepAction = createActionCreator(ClaimsStateTypes.SetCurrentStep);
export const initWizardAction = createActionCreator(ClaimsStateTypes.InitWizard);
export const cancelClaimActions = createApiActionCreators(ClaimsStateTypes.CancelClaim);
export const resetWizardAction = createActionCreator(ClaimsStateTypes.ResetWizard);
export const setInitialized = createActionCreator(ClaimsStateTypes.SetInitialized);

/* REDUCERS */
const initialState: ClaimsState = {
  invoiceNumber: null,
  invoiceNumberOld: null,
  claimId: null,
  claimIdOld: null,
  serviceId: null,
  caRefNo: null,
  currentStep: null,
  canContinue: false,
  currentData: {},
  loadingStatus: LoadingStatus.loading,
  initialized: false,
  fromSummary: false,
  cancelClaimModal: false,
  changeData: {},
};

const invoiceNumber = createReducer(initialState.invoiceNumber, {
  [ClaimsStateTypes.SetInvoiceNumber]: (state: string | null, payload: string | null) => payload,
  [ClaimsStateTypes.ResetWizard]: () => null,
});

const invoiceNumberOld = createReducer(initialState.invoiceNumberOld, {
  [ClaimsStateTypes.SetInvoiceNumberOld]: (state: string | null, payload: string | null) => payload,
});

const claimIdOld = createReducer(initialState.claimIdOld, {
  [ClaimsStateTypes.SetClaimIdOld]: (state: number | null, payload: number | null) => payload,
});

const claimId = createReducer(initialState.claimId, {
  [ClaimsStateTypes.SetClaimId]: (state: number | null, payload: number | null) => payload,
  [ClaimStep1StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step1ViewVO, payload: Step1ViewVO) => payload.claimInfo?.id,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ClaimsStateTypes.ResetWizard]: () => null,
});
const serviceId = createReducer(initialState.serviceId, {
  [ClaimsStateTypes.SetServiceId]: (state: number | null, payload: number | null) => payload,
  [ClaimStep1StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step1ViewVO, payload: Step1ViewVO) =>
      payload?.claimInfo?.claimServiceId || state || null,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ClaimStep1StateTypes.SaveClaimStep1]: {
    [RequestActionTypes.SUCCESS]: (state: number | null, payload: ClaimActionResponseVO) => payload?.serviceId,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ClaimsStateTypes.ResetWizard]: () => null,
});

const caRefNo = createReducer(initialState.caRefNo, {
  [ClaimsStateTypes.SetCaRefNo]: (state: string | null, payload: string | null) => payload,
  [ClaimStep1StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: string | null, payload: Step1ViewVO) =>
      payload?.invoiceInfo?.customerAccountNo || null,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ClaimStep2StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: string | null, payload: Step2ViewVO) =>
      payload?.invoiceInfo?.customerAccountNo || null,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ClaimStep3StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: string | null, payload: Step3ViewVO) =>
      payload?.invoiceInfo?.customerAccountNo || null,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ClaimStep4StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: string | null, payload: Step4ViewVO) =>
      payload?.invoiceInfo?.customerAccountNo || null,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ClaimStep5StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: string | null, payload: Step5ViewVO) =>
      payload?.invoiceInfo?.customerAccountNo || null,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ClaimStep6StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: string | null, payload: Step6ViewVO) =>
      payload?.invoiceInfo?.customerAccountNo || null,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ClaimsStateTypes.ResetWizard]: () => null,
});

const currentStep = createReducer(initialState.currentStep, {
  [ClaimsStateTypes.SetCurrentStep]: (state: number, payload: ChangeStepPayload) => payload.step || 1,
  [ClaimsStateTypes.ResetWizard]: () => null,
});

const changeData = createReducer(initialState.changeData, {
  [ClaimStep6StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step6ViewVO, payload: Step6ViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
});

const canContinue = createReducer(initialState.canContinue, {
  [ClaimsStateTypes.SetCanContinue]: (state: boolean, payload: boolean) => payload,
  [ClaimsStateTypes.SetCurrentStep]: () => false,
  [ClaimStep1StateTypes.SaveClaimStep1]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [ClaimStep2StateTypes.SaveClaimStep2]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [ClaimStep3StateTypes.SaveClaimStep3]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [ClaimStep4StateTypes.SaveClaimStep4]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [ClaimStep5StateTypes.SaveClaimStep5]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [ClaimStep6StateTypes.SendClaim]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [ClaimStep6StateTypes.AssignClaim]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [ClaimStep6StateTypes.ProcessClaim]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [ClaimsStateTypes.ResetWizard]: () => false,
});

const currentData = createReducer(initialState.currentData, {
  [ClaimsStateTypes.SetCurrentStep]: (state: any) => {
    if (state) {
      return {};
    }
  },
  [ClaimStep1StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step1ViewVO, payload: Step1ViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
  [ClaimStep1StateTypes.GetLazyLoad]: {
    [RequestActionTypes.SUCCESS]: (state: Step1ViewVO, payload: Step1ViewVO) => {
      payload.serviceRows = [...(state.serviceRows || []), ...(payload.serviceRows || [])];
      state.lazyLoadIndicator = payload.lazyLoadIndicator;

      return {
        ...state,
        ...payload,
      };
    },
    [RequestActionTypes.FAILURE]: (state: Step1ViewVO) => {
      state.lazyLoadIndicator = false;

      return {
        ...state,
      };
    },
  },
  [ClaimStep2StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step2ViewVO, payload: Step2ViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
  [ClaimStep3StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step3ViewVO, payload: Step3ViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
  [ClaimStep4StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step4ViewVO, payload: Step4ViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
  [ClaimStep4StateTypes.UploadFileStep4]: {
    [RequestActionTypes.SUCCESS]: (state: Step4ViewVO, payload: ClaimFileResponseVO) => {
      if (payload?.attachment) {
        state.claimAttachments?.push(payload?.attachment);
      }

      return {
        ...state,
      };
    },
  },
  [ClaimStep5StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step5ViewVO, payload: Step5ViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
  [ClaimStep6StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step6ViewVO, payload: Step6ViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
  [ClaimStep7StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Step7ViewVO, payload: Step7ViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
  [ClaimsStateTypes.ResetWizard]: () => {
    return {};
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [ClaimStep1StateTypes.GetData]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
    [ClaimStep1StateTypes.GetLazyLoad]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
    [ClaimStep2StateTypes.GetData]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
    [ClaimStep7StateTypes.SendEmailAgain]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
    [ClaimStep7StateTypes.ReturnBack]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [ClaimStep2StateTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
    [ClaimStep7StateTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const initialized = createReducer(initialState.initialized, {
  [ClaimsStateTypes.SetInitialized]: (state: boolean, payload: boolean) => payload,
});

const fromSummary = createReducer(initialState.fromSummary, {
  [ClaimsStateTypes.SetFromSummary]: (state: boolean, payload: boolean) => payload,
  [ClaimsStateTypes.SetCurrentStep]: (state: boolean, payload: ChangeStepPayload) => !!payload.fromSummary,
});

const cancelClaimModal = createReducer(initialState.invoiceNumber, {
  [ClaimsStateTypes.CancelClaim]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [ClaimStep6StateTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: () => false,
  },
});

export default combineReducers<ClaimsState>({
  invoiceNumber,
  invoiceNumberOld,
  claimId,
  claimIdOld,
  serviceId,
  caRefNo,
  currentStep,
  canContinue,
  currentData,
  loadingStatus,
  initialized,
  fromSummary,
  cancelClaimModal,
  changeData,
});

/* SELECTORS */
const selectClaimsState = (state: AppState) => state.claims;

export const selectInvoiceNumber = (state: AppState): string | null => selectClaimsState(state).invoiceNumber;
export const selectInvoiceNumberOld = (state: AppState): string | null => selectClaimsState(state).invoiceNumberOld;
export const selectClaimId = (state: AppState): number | null => selectClaimsState(state).claimId;
export const selectClaimIdOld = (state: AppState): number | null => selectClaimsState(state).claimIdOld;
export const selectServiceId = (state: AppState): number | null => selectClaimsState(state).serviceId;
export const selectCurrentStep = (state: AppState): number | null => selectClaimsState(state).currentStep;
export const selectCanContinue = (state: AppState): boolean => selectClaimsState(state).canContinue;
export const selectCurrentData = (state: AppState): any => selectClaimsState(state).currentData;
export const selectChangeData = (state: AppState): any => selectClaimsState(state).changeData;
export const selectClaimCaRefNo = (state: AppState): string | null | undefined => selectClaimsState(state).caRefNo;
export const selectClaimDataLoading = (state: AppState): LoadingStatus => selectClaimsState(state).loadingStatus;
export const selectIsWizardInitialized = (state: AppState): boolean => selectClaimsState(state).initialized;
export const selectIsFromSummary = (state: AppState): boolean => selectClaimsState(state).fromSummary;
export const selectCancelClaimModal = (state: AppState): boolean => selectClaimsState(state).cancelClaimModal;

/* SAGAS */

function* initWizard() {
  yield put(setInitialized(false));

  const currentStep: number | null = yield select(selectCurrentStep);
  const invoiceNumber: string | null = yield select(selectInvoiceNumber);
  const claimId: number | null = yield select(selectClaimId);
  const serviceId: number | null = yield select(selectServiceId);
  const caRefNo: string | null | undefined = yield select(selectClaimCaRefNo);
  const urlParams = new URLSearchParams(window.location.search);
  const urlCaRefNo = urlParams.get('billingCaRefNo');
  const urlInvoiceNumber = urlParams.get('invoicenumber');
  const urlClaimId = Number(urlParams.get('claimId'));
  const urlServiceId = Number(urlParams.get('serviceId'));

  // Set invoiceNumber || claimId || serviceId if not set
  if (!invoiceNumber) {
    yield put(setInvoiceNumberAction(urlInvoiceNumber));
  }

  if (!claimId && urlClaimId) {
    yield put(setClaimIdAction(urlClaimId));
  }

  if (!serviceId && urlServiceId) {
    yield put(setServiceIdAction(urlServiceId));
  }

  if (!caRefNo) {
    yield put(setCaRefNoAction(urlCaRefNo));
  }

  const claimSteps: Record<number, string> = {
    1: BillingManagementClaimStepsRoute.ClaimStep1,
    2: BillingManagementClaimStepsRoute.ClaimStep2,
    3: BillingManagementClaimStepsRoute.ClaimStep3,
    4: BillingManagementClaimStepsRoute.ClaimStep4,
    5: BillingManagementClaimStepsRoute.ClaimStep5,
    6: BillingManagementClaimStepsRoute.ClaimStep6,
    7: BillingManagementClaimStepsRoute.ClaimStep7,
  };
  const nextStep = Number(
    (Object.keys(claimSteps) as Array<string>).find((key: any) => claimSteps[key] === window.location.pathname)
  );

  if (currentStep !== nextStep) {
    yield put(
      setCurrentStepAction({
        step: nextStep,
        invoiceNumber: invoiceNumber || urlInvoiceNumber,
        claimId: claimId || urlClaimId,
        serviceId: serviceId || urlServiceId,
      })
    );
  }

  yield put(setInitialized(true));
}

function* setCurrentStep({ payload }: AppAction<ChangeStepPayload>) {
  const init: boolean = yield select(selectIsWizardInitialized);

  if (!init) {
    return;
  }

  const paramString = `invoicenumber=${payload.invoiceNumber}&claimId=${payload.claimId}&serviceId=${payload.serviceId}`;
  const searchParams = new URLSearchParams(paramString);

  if (!payload.invoiceNumber) {
    searchParams.delete('invoicenumber');
  }

  if (!payload.claimId) {
    searchParams.delete('claimId');
  }

  if (!payload.serviceId) {
    searchParams.delete('serviceId');
  }

  const finalSearchParams = searchParams.toString();

  if (payload.resetWizard) {
    yield putResolve(resetWizardAction());
  }

  switch (payload.step) {
    case 1:
      history.replace({
        pathname: BillingManagementClaimStepsRoute.ClaimStep1,
        search: finalSearchParams,
      });

      break;
    case 2:
      history.replace({
        pathname: BillingManagementClaimStepsRoute.ClaimStep2,
        search: finalSearchParams,
      });

      break;
    case 3:
      history.replace({
        pathname: BillingManagementClaimStepsRoute.ClaimStep3,
        search: finalSearchParams,
      });

      break;
    case 4:
      history.replace({
        pathname: BillingManagementClaimStepsRoute.ClaimStep4,
        search: finalSearchParams,
      });

      break;
    case 5:
      history.replace({
        pathname: BillingManagementClaimStepsRoute.ClaimStep5,
        search: finalSearchParams,
      });

      break;
    case 6:
      history.replace({
        pathname: BillingManagementClaimStepsRoute.ClaimStep6,
        search: finalSearchParams,
      });

      break;
    case 7:
      history.replace({
        pathname: BillingManagementClaimStepsRoute.ClaimStep7,
        search: finalSearchParams,
      });

      break;
    default:
      break;
  }

  if (payload.resetWizard) {
    yield putResolve(initWizardAction());
  }
}

function* cancelClaim({ payload }: AppAction<ClaimsCancelClaimPayload>) {
  const currentStep: number | null = yield select(selectCurrentStep);
  const invoiceNumber: string = yield select(selectInvoiceNumber);
  const invoiceNumberOld: string = yield select(selectInvoiceNumberOld);
  const invoiceNumExt: string = yield select(selectFinancialDocumentInvoiceNumExt);
  const caRefNo: string = yield select(selectBillingCaRefNo);
  const claimId: number = yield select(selectClaimId);
  const claimIdOld: number = yield select(selectClaimIdOld);
  const serviceId: number = yield select(selectServiceId);

  if (!currentStep) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.cancelClaim,
      yield getApiUrl(environment.urlConfig.billingManagement),
      currentStep,
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(cancelClaimActions.success());

    if (resp?.data?.kahl) {
      yield put(getKahlActions.request({ infoMessageIds: resp.data.kahl }));
    }

    switch (currentStep) {
      case 1:
        if (resp.data.id === payload.claimId) {
          yield put(
            push(
              `${buildRoute(ROUTES.BillingDetail, {
                page: 'month-statement-detail',
              })}?invoiceNumExt=${invoiceNumExt}&billingCaRefNo=${payload.caRefNo || caRefNo}`
            )
          );
        } else {
          yield put(
            setCurrentStepAction({
              step: 6,
              invoiceNumber: resp.data.invoiceNo || invoiceNumberOld,
              claimId: resp.data.id || claimIdOld,
              serviceId:
                resp.data.serviceId ||
                (payload?.serviceAlreadyClaimed?.length
                  ? payload?.serviceAlreadyClaimed[payload?.serviceAlreadyClaimed?.length - 1]
                  : undefined),
            })
          );
          yield put(
            getClaimStep6DataActions.request({
              invoiceNumber: resp.data.invoiceNo || invoiceNumberOld,
              claimId: resp.data.id || claimIdOld,
              serviceId:
                resp.data.serviceId ||
                (payload?.serviceAlreadyClaimed?.length
                  ? payload?.serviceAlreadyClaimed[payload?.serviceAlreadyClaimed?.length - 1]
                  : undefined),
            })
          );
        }

        break;
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
        if (payload.claimCount && payload.claimCount > 1) {
          yield put(
            setCurrentStepAction({
              step: 6,
              invoiceNumber: invoiceNumber,
              claimId: claimId,
              serviceId: serviceId,
            })
          );
          yield put(
            getClaimStep6DataActions.request({
              invoiceNumber: invoiceNumber,
              claimId: claimId,
              serviceId: serviceId,
            })
          );
        } else {
          yield put(
            push(
              `${buildRoute(ROUTES.BillingDetail, {
                page: 'month-statement-detail',
              })}?invoiceNumExt=${invoiceNumExt}&billingCaRefNo=${payload.caRefNo || caRefNo}`
            )
          );
        }

        break;
    }
  } else {
    yield put(cancelClaimActions.failure());
  }
}

/* EXPORT */
export function* claimsSaga() {
  yield takeLatest(createActionType(ClaimsStateTypes.InitWizard), initWizard);
  yield takeLatest(createActionType(ClaimsStateTypes.SetCurrentStep), setCurrentStep);
  yield takeLatest(createActionType(ClaimsStateTypes.CancelClaim, RequestActionTypes.REQUEST), cancelClaim);
}
